/* DRINK MILK: info pages — splits the shop's text into numbered sections at its bold lead-ins
   ("<strong>Heading</strong> - text"), builds the contents list, the reading-progress line and the gentle motion.
   Ported from the prototype (wedding-site/src/legal.js); the wording itself is never changed. */
(function () {
  const page = document.querySelector('.dm-lg');
  if (!page) return;
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  gsap.registerPlugin(ScrollTrigger);

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const pad = (n) => String(n).padStart(2, '0');
  const strip = (s) => s.replace(/<\/?(strong|b)>/gi, '').trim();

  /* ---------- From the shop's text to sections ---------- */

  const source = $('.lg-source', page);
  // Text pasted from Word or other sites carries spans, styles and nested divs: keep only the words,
  // bold, line breaks and links, so the lead-ins can be found
  const tidy = (html) =>
    html
      .replace(/<(\/?)(strong|b|br|a)\b([^>]*)>/gi, (m, close, tag, attrs) => {
        tag = tag.toLowerCase() === 'b' ? 'strong' : tag.toLowerCase();
        const href = !close && tag === 'a' && (attrs.match(/href="[^"]*"/) || [''])[0];
        return `\u0001${close}${tag}${href ? ` ${href}` : ''}\u0002`;
      })
      .replace(/<[^>]+>/g, '')
      .replace(/\u0001/g, '<')
      .replace(/\u0002/g, '>')
      .replace(/&nbsp;| /g, ' ')
      .replace(/<\/strong>(\s*)<strong>/g, '$1')
      .replace(/<strong>\s*<\/strong>/g, '')
      .replace(/[ \t]+/g, ' ')
      .trim();
  const all = $$('p, h2, h3, h4, h5, h6, ul, ol', source);
  const blocks = all
    .filter((el) => !all.some((other) => other !== el && other.contains(el)))
    .map((el) => ({ tag: /^H[56]$/.test(el.tagName) ? 'H4' : el.tagName, html: /^(UL|OL)$/.test(el.tagName) ? el.innerHTML : tidy(el.innerHTML) }))
    .filter((b) => b.html && b.html !== '<br>');

  let title = '';
  let updated = '';
  const intro = [];
  const sections = [];
  let cur = null;
  // After a section that was one fully bold block, plain paragraphs start a new section without a heading
  let closeAfter = false;
  const lines = (html) => html.split(/<br\s*\/?>/i).map((l) => l.trim()).filter(Boolean);

  blocks.forEach((b, i) => {
    const html = b.html;
    let m;
    if (b.tag === 'UL' || b.tag === 'OL') {
      (cur ? cur.body : intro).push(`<${b.tag.toLowerCase()}>${html}</${b.tag.toLowerCase()}>`);
      return;
    }
    // First bold line is the page title, maybe with "Last updated:" under it
    if (i === 0 && (m = html.match(/^<(strong|b)>([^<]*)<\/\1>(?:\s*<br\s*\/?>([\s\S]*))?$/i)) && m[2].trim().length <= 40) {
      title = m[2].trim();
      const u = (m[3] || '').match(/(?:Last updated|Updated|Effective date):\s*(.*)/i);
      if (u) updated = strip(u[1]);
      return;
    }
    // Headings written as h2–h4: a line ending with ":" starts a section, the rest is its text
    if (/^H[234]$/.test(b.tag)) {
      const text = strip(html.replace(/<br\s*\/?>/gi, ' '));
      const ls = lines(html);
      if (ls.length > 1 && /[:：]\s*$/.test(strip(ls[0]))) {
        cur = { h: strip(ls[0]).replace(/[:：]\s*$/, ''), body: ls.slice(1) };
        sections.push(cur);
      } else if (/[:：]\s*$/.test(text) || (b.tag !== 'H4' && ls.length === 1)) {
        cur = { h: text.replace(/[:：]\s*$/, ''), body: [] };
        sections.push(cur);
      } else if (b.tag !== 'H4' && lines(html).length > 1) {
        const [h, ...rest] = lines(html);
        cur = { h: strip(h), body: rest.map(strip) };
        sections.push(cur);
      } else {
        (cur ? cur.body : intro).push(...lines(html).map((l) => l));
      }
      closeAfter = false;
      return;
    }
    // A whole bold block: first line is the heading, the rest its text
    if ((m = html.match(/^<(strong|b)>([^<]*?)<br\s*\/?>([\s\S]*)<\/\1>$/i))) {
      cur = { h: m[2].trim(), body: lines(m[3]).map(strip) };
      sections.push(cur);
      closeAfter = true;
      return;
    }
    // "<strong>Heading</strong><br>text"
    if ((m = html.match(/^<(strong|b)>([^<]*?)<\/\1>\s*<br\s*\/?>([\s\S]*)$/i))) {
      cur = { h: m[2].trim(), body: lines(m[3]) };
      sections.push(cur);
      closeAfter = false;
      return;
    }
    // "<strong>Heading</strong> - text" or "<strong>Heading:</strong> text"
    if ((m = html.match(/^<(strong|b)>([^<]*?):?<\/\1>:?\s*(?:[-–]\s*)?([\s\S]*)$/i)) && m[2].trim().length < 60) {
      cur = { h: m[2].trim().replace(/:$/, ''), body: m[3].trim() ? [m[3].trim()] : [] };
      sections.push(cur);
      closeAfter = false;
      return;
    }
    if (closeAfter) {
      cur = { h: '', body: [] };
      sections.push(cur);
      closeAfter = false;
    }
    (cur ? cur.body : intro).push(...lines(html));
  });

  // Text with no bold lead-ins at all stays as it is
  const article = $('.lg-article', page);
  const help = $('.lg-help-template', page).content.cloneNode(true);
  if (!sections.length) {
    source.classList.add('lg-intro');
    article.append(help);
  } else {
    let n = 0;
    article.innerHTML =
      (intro.length ? `<div class="lg-intro">${intro.map((t) => (/^<(ul|ol)/.test(t) ? t : `<p>${t}</p>`)).join('')}</div>` : '') +
      sections
        .map((s) => {
          if (!s.h) return `<section class="lg-section">${s.body.map((t) => `<p>${t}</p>`).join('')}</section>`;
          n += 1;
          return `<section class="lg-section" id="s${n}" aria-labelledby="s${n}-h">
            <h2 class="lg-section__title" id="s${n}-h"><span class="lg-section__num mono ltr" aria-hidden="true">${pad(n)}</span>${s.h}</h2>
            ${s.body.map((t) => (/^<(ul|ol)/.test(t) ? t : `<p>${t}</p>`)).join('')}
          </section>`;
        })
        .join('');
    article.append(help);
  }
  const active = $('.shop-tab.is-active', page);
  // The page's own bold title, else the Hebrew name of its tab (Shopify's policy titles are in English)
  if (title || active) $('.shop-hero__title', page).textContent = title || active.textContent.trim();
  $('.lg-updated', page).textContent = updated ? `Last updated: ${updated}` : '';
  $('.shop-hero__marquee span', page).textContent = `${active?.dataset.word || 'INFO'} ✦ `.repeat(8);
  active?.scrollIntoView({ block: 'nearest', inline: 'center' });

  const heads = $$('.lg-section[id]', page);
  $('.lg-toc__list', page).innerHTML = heads
    .map((s, i) => `<li><a href="#${s.id}" data-target="${s.id}"><span class="mono ltr">${pad(i + 1)}</span>${$('h2', s).lastChild.textContent}</a></li>`)
    .join('');
  if (!heads.length) $('.lg-toc', page).hidden = true;

  // Desktop shows the contents open beside the text; phones get it folded behind a button
  const toc = $('.lg-toc__box', page);
  const wide = matchMedia('(min-width: 900px)');
  const syncToc = () => { toc.open = wide.matches; };
  syncToc();
  wide.addEventListener('change', syncToc);

  /* ---------- Contents links, progress line, current section ---------- */

  let lenis = null;
  const offset = () => -(($('.dm-header')?.offsetHeight || 0) + $('.lg-bar', page).offsetHeight + 24);
  $('.lg-toc__list', page).addEventListener('click', (e) => {
    const a = e.target.closest('a');
    if (!a) return;
    e.preventDefault();
    const target = document.getElementById(a.dataset.target);
    history.replaceState(null, '', `#${a.dataset.target}`);
    // Fold the list first, so the section is measured where it will really be
    if (!wide.matches) toc.open = false;
    if (lenis) lenis.scrollTo(target, { offset: offset(), duration: 1 });
    else scrollTo(0, target.getBoundingClientRect().top + scrollY + offset());
  });

  const links = $$('.lg-toc__list a', page);
  const mark = (id) => links.forEach((l) => l.classList.toggle('is-current', l.dataset.target === id));
  heads.forEach((sec) => {
    ScrollTrigger.create({ trigger: sec, start: 'top 40%', end: 'bottom 40%', onToggle: (self) => self.isActive && mark(sec.id) });
  });
  ScrollTrigger.create({
    trigger: article,
    start: 'top 60%',
    end: 'bottom bottom',
    onUpdate: (self) => gsap.set('.dm-lg .lg-progress span', { scaleX: self.progress }),
  });

  /* ---------- Motion ---------- */

  const shell = window.DMShell;
  const mm = gsap.matchMedia();
  mm.add('(prefers-reduced-motion: no-preference)', () => {
    lenis = new window.Lenis({ autoRaf: false });
    lenis.on('scroll', ScrollTrigger.update);
    shell?.setLenis(lenis);
    const raf = (t) => lenis.raf(t * 1000);
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);

    gsap.from('.dm-lg .shop-hero__title, .dm-lg .lg-updated', { y: 30, opacity: 0, duration: 0.9, stagger: 0.1, ease: 'power4.out' });
    gsap.from('.dm-lg .shop-hero__marquee', { xPercent: 12, opacity: 0, duration: 1.4, ease: 'power3.out' });
    gsap.fromTo('.dm-lg .shop-hero__marquee span', { xPercent: 0 }, { xPercent: -50, duration: 50, ease: 'none', repeat: -1 });

    // Sections rise in softly as they reach the screen; the text itself stays still once it's there
    gsap.set('.dm-lg .lg-intro, .dm-lg .lg-section, .dm-lg .lg-help', { opacity: 0, y: 30 });
    ScrollTrigger.batch('.dm-lg .lg-intro, .dm-lg .lg-section, .dm-lg .lg-help', {
      start: 'top 90%',
      once: true,
      onEnter: (batch) => gsap.to(batch, { opacity: 1, y: 0, duration: 0.8, stagger: 0.08, ease: 'power3.out' }),
    });
    gsap.from('.dm-lg .lg-toc__list li', { x: 20, opacity: 0, duration: 0.6, stagger: 0.04, ease: 'power3.out', delay: 0.3 });

    if (location.hash && document.getElementById(location.hash.slice(1))) {
      requestAnimationFrame(() => lenis.scrollTo(location.hash, { offset: offset(), immediate: true }));
    }

    return () => {
      gsap.ticker.remove(raf);
      lenis.destroy();
      lenis = null;
      shell?.setLenis(null);
    };
  });

  addEventListener('load', () => ScrollTrigger.refresh());
})();
